import type {
    LegacyCheckoutStatus,
    OrderStatus,
    PaymentStatus,
    RedirectionInstructions,
} from '@eventbrite/checkout-fairy-api';
import logger from '@eventbrite/client-logger';
import { CustomSubmissionError } from '@eventbrite/redux-form-tools';
import { asyncWait } from '@eventbrite/wait';
import { OrderDocumentStatus, TlzEnvironment } from '../../api/constants';
import { getOrderComposedStatus, getOrderStatus } from '../../api/order';
import { getEllapsedTimeInMillisecondsFactory } from '../../utils/time';
import {
    ORDER_STATUS_POLLING_PERIOD_IN_MS,
    ORDER_STATUS_POLLING_TIMEOUT_IN_MS,
    SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS,
} from './constants';

class CheckoutPollingError extends Error {
    constructor(message: string, options?: any) {
        super(message, options);
        Object.setPrototypeOf(this, CheckoutPollingError.prototype);
        this.name = 'CheckoutPollingError';
    }
}

const orderProcessedStatuses = new Set<OrderStatus>([
    OrderDocumentStatus.abandoned,
    OrderDocumentStatus.cancelled,
    OrderDocumentStatus.draft,
    OrderDocumentStatus.finalized,
]);

const isOrderProcessed = (status: OrderStatus | null) =>
    status && orderProcessedStatuses.has(status);

const isOrderWaitingForExternalPaymentCompletion = (
    status: OrderStatus | null,
) => status === OrderDocumentStatus.confirmed;

const getTimer = () => {
    const getEllapsedTimeInMilliseconds =
        getEllapsedTimeInMillisecondsFactory();

    const isTimerExpired = () =>
        getEllapsedTimeInMilliseconds() > ORDER_STATUS_POLLING_TIMEOUT_IN_MS;
    const isPollingBeingSlow = () =>
        getEllapsedTimeInMilliseconds() >
        SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS;

    return {
        getEllapsedTimeInMilliseconds,
        isTimerExpired,
        isPollingBeingSlow,
    };
};

export const waitUntilOrderIsProcessed = async ({
    eventId,
    env,
    isNosScenarioFive,
    orderId,
    onRedirectionInstructionsAvailable,
    onPaymentStatusAvailable,
}: {
    env: TlzEnvironment;
    eventId: string;
    isNosScenarioFive: boolean;
    orderId: string;
    onRedirectionInstructionsAvailable: (
        redirectionInstructions: RedirectionInstructions,
    ) => void;
    onPaymentStatusAvailable: (paymentStatus: PaymentStatus) => void;
}) => {
    const {
        getEllapsedTimeInMilliseconds,
        isPollingBeingSlow,
        isTimerExpired,
    } = getTimer();

    let lastOrderStatusResponse: {
        orderStatus: OrderStatus | null;
        redirectionInstructions: RedirectionInstructions | null;
        paymentStatus: PaymentStatus | null;
        status: LegacyCheckoutStatus | null;
    } = {
        orderStatus: null,
        redirectionInstructions: null,
        paymentStatus: null,
        status: null,
    };

    let hasCalledOnRedirectionInstructionsAvailable = false;

    const getErrorMetadata = () => ({
        total_polling_time_ms: getEllapsedTimeInMilliseconds(),
    });
    const getErrorTags = () => ({
        order_id: orderId,
        last_order_status: lastOrderStatusResponse.orderStatus,
        total_polling_time_seconds: Math.round(
            getEllapsedTimeInMilliseconds() / 1000,
        ),
    });

    while (!isTimerExpired()) {
        const pollingPeriodPromise = asyncWait(
            ORDER_STATUS_POLLING_PERIOD_IN_MS,
        );

        const getOrderStatusMethod = isNosScenarioFive
            ? getOrderComposedStatus
            : getOrderStatus;

        try {
            lastOrderStatusResponse = await getOrderStatusMethod({
                env,
                eventId,
                orderId,
            });
        } catch (error) {
            lastOrderStatusResponse = {
                orderStatus: null,
                redirectionInstructions: null,
                paymentStatus: null,
                status: null,
            };

            logger.error(
                new CheckoutPollingError('Error polling CheckoutStatusAPI'),
                {
                    ...getErrorMetadata(),
                    cause: error.message,
                    rawError: JSON.stringify(error),
                },
                getErrorTags(),
            );
        }

        onPaymentStatusAvailable(lastOrderStatusResponse.paymentStatus);

        if (isOrderProcessed(lastOrderStatusResponse.orderStatus)) {
            if (isPollingBeingSlow()) {
                logger.error(
                    new CheckoutPollingError(
                        `The order took ${getEllapsedTimeInMilliseconds()}ms to finish with status ${
                            lastOrderStatusResponse.orderStatus
                        }`,
                    ),
                    getErrorMetadata(),
                    getErrorTags(),
                );
            }

            return lastOrderStatusResponse.orderStatus;
        }

        const shouldCallWaitForExternalPaymentCompletion =
            isNosScenarioFive &&
            !hasCalledOnRedirectionInstructionsAvailable &&
            isOrderWaitingForExternalPaymentCompletion(
                lastOrderStatusResponse.orderStatus,
            );

        if (
            shouldCallWaitForExternalPaymentCompletion &&
            lastOrderStatusResponse.redirectionInstructions
        ) {
            hasCalledOnRedirectionInstructionsAvailable = true;
            onRedirectionInstructionsAvailable(
                lastOrderStatusResponse.redirectionInstructions,
            );
        }

        if (isPollingBeingSlow()) {
            logger.error(
                new CheckoutPollingError(
                    `The order is taking more than ${SLOW_ORDER_STATUS_POLLING_DURATION_IN_MS}ms to finish (last status was ${lastOrderStatusResponse.orderStatus})`,
                ),
                getErrorMetadata(),
                getErrorTags(),
            );
        }

        await pollingPeriodPromise;
    }

    logger.error(
        new CheckoutPollingError(
            `The order did not transition to complete, cancelled or error status on time (last status was ${lastOrderStatusResponse.orderStatus})`,
        ),
        getErrorMetadata(),
        getErrorTags(),
    );

    throw new CustomSubmissionError(
        {
            _error: 'There was a problem submitting your order. Please try again.',
        },
        {},
    );
};
